import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnChanges, SimpleChanges, inject, signal } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  GenerateGrowthImprovementRecommendationsResponse,
  GrowthJourneyImprovementRecommendation,
  GrowthJourneyService
} from './growth-journey.service';
import {
  growthJourneyOptimizerTypeI18nKey,
  improvementRecommendationStatusI18nKey,
  improvementRecommendationStatusSeverity as resolveRecStatusSeverity,
  improvementRiskBandI18nKey,
  improvementRiskBandSeverity as resolveRiskBandSeverity,
  normalizeImprovementRecommendationStatus,
  parseImprovementRationale,
  swotProviderI18nKey
} from './growth-status.util';

@Component({
  standalone: true,
  selector: 'ain-growth-journey-improvement-panel',
  providers: [MessageService],
  imports: [InfoHintComponent, AinStateComponent, ButtonModule, TableModule, TagModule],
  template: `
    <section id="improvement" class="improvement-panel card">
      <h3>{{ t('growth.journey.improvement.title') }}</h3>

      @if (!available) {
        <ain-info-hint
          [titleKey]="'growth.journey.monitoring.unavailable.title'"
          [descriptionKey]="'growth.journey.monitoring.unavailable.description'" />
      } @else {
        <ain-info-hint
          [titleKey]="'growth.journey.improvement.help.title'"
          [descriptionKey]="'growth.journey.improvement.help.description'"
          [bulletKeys]="[
            'growth.journey.improvement.advisory',
            'growth.journey.improvement.help.bullets.accept'
          ]" />

        <div class="toolbar">
          @if (canRecommend()) {
            <button
              pButton
              type="button"
              [label]="t('growth.journey.improvement.generate')"
              [loading]="generating()"
              (click)="generate()"></button>
          }
          <button
            pButton
            type="button"
            severity="secondary"
            [label]="t('common.refresh')"
            [loading]="loading()"
            (click)="load()"></button>
        </div>

        @if (lastGenerateResult(); as result) {
          <div class="meta-row">
            <p-tag [value]="providerLabel(result.provider)" severity="info" />
            @if (result.usedFallback) {
              <p-tag [value]="t('growth.journey.improvement.fallbackUsed')" severity="warn" />
            }
          </div>
          @if (result.overallAssessment) {
            <p class="overall-assessment">
              <strong>{{ t('growth.journey.improvement.overallAssessment') }}:</strong>
              {{ result.overallAssessment }}
            </p>
          }
        }

        @if (loading()) {
          <ain-state type="loading" [title]="t('common.loading')" />
        } @else if (error()) {
          <ain-state type="error" [title]="t('common.error')" [message]="error() ?? ''" />
        } @else if (recommendations().length === 0) {
          <ain-state type="empty" [title]="t('growth.journey.improvement.empty')" />
        } @else {
          <div class="recommendation-list">
            @for (rec of recommendations(); track rec.id) {
              <article class="recommendation-card">
                <div class="recommendation-header">
                  <div class="recommendation-title">
                    <strong>{{ rec.title }}</strong>
                    <p-tag
                      [value]="t(optimizerTypeLabelKey(rec.optimizerType))"
                      severity="contrast" />
                    <p-tag
                      [value]="t(recommendationStatusLabelKey(rec.status))"
                      [severity]="recommendationStatusSeverity(rec.status)" />
                  </div>
                  <div class="meta-row">
                    <p-tag [value]="providerLabel(rec.aiProvider)" severity="info" />
                    @if (rec.usedFallback) {
                      <p-tag [value]="t('growth.journey.improvement.fallbackUsed')" severity="warn" />
                    }
                    <p-tag
                      [value]="t(riskBandLabelKey(rec.riskBand))"
                      [severity]="riskBandSeverity(rec.riskBand)" />
                    <span class="confidence">
                      {{ t('growth.journey.improvement.fields.confidence') }}:
                      {{ formatConfidence(rec.confidence) }}
                    </span>
                  </div>
                </div>

                @if (rationale(rec); as text) {
                  <p class="recommendation-rationale">
                    <strong>{{ t('growth.journey.improvement.fields.rationale') }}:</strong>
                    {{ text }}
                  </p>
                }

                @if (rec.expectedImpact) {
                  <p class="recommendation-impact">
                    <strong>{{ t('growth.journey.improvement.fields.expectedImpact') }}:</strong>
                    {{ rec.expectedImpact }}
                  </p>
                }

                @if (canRecommend() && isAdvisory(rec.status)) {
                  <div class="recommendation-actions">
                    <button
                      pButton
                      type="button"
                      size="small"
                      [label]="t('growth.journey.improvement.accept')"
                      [loading]="acceptingId() === rec.id"
                      (click)="accept(rec)"></button>
                    <button
                      pButton
                      type="button"
                      size="small"
                      severity="secondary"
                      [label]="t('growth.journey.improvement.dismiss')"
                      [loading]="dismissingId() === rec.id"
                      (click)="dismiss(rec)"></button>
                  </div>
                }
              </article>
            }
          </div>
        }
      }
    </section>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; display: grid; gap: 10px; margin-bottom: 12px; }
    .card h3 { margin: 0; font-size: 1rem; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .meta-row { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .overall-assessment { margin: 0; }
    .recommendation-list { display: grid; gap: 10px; }
    .recommendation-card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 10px; display: grid; gap: 8px; }
    .recommendation-header { display: grid; gap: 8px; }
    .recommendation-title { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .recommendation-rationale, .recommendation-impact { margin: 0; color: #374151; }
    .recommendation-actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .confidence { font-size: 0.85rem; color: #6b7280; }
  `]
})
export class GrowthJourneyImprovementPanelComponent implements OnChanges {
  private readonly journeyService = inject(GrowthJourneyService);
  private readonly i18n = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly messages = inject(MessageService);

  @Input({ required: true }) journeyId!: string;
  @Input() available = false;

  readonly loading = signal(false);
  readonly generating = signal(false);
  readonly acceptingId = signal<string | null>(null);
  readonly dismissingId = signal<string | null>(null);
  readonly error = signal<string | null>(null);
  readonly recommendations = signal<GrowthJourneyImprovementRecommendation[]>([]);
  readonly lastGenerateResult = signal<GenerateGrowthImprovementRecommendationsResponse | null>(null);

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['journeyId'] || changes['available']) && this.available && this.journeyId) {
      this.load();
    }
  }

  load(): void {
    if (!this.journeyId || !this.available) {
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    this.journeyService.listRecommendations(this.journeyId).subscribe({
      next: items => {
        this.recommendations.set(items);
        this.loading.set(false);
      },
      error: err => {
        this.error.set(this.resolveError(err));
        this.loading.set(false);
      }
    });
  }

  generate(): void {
    if (!this.journeyId || !this.canRecommend()) {
      return;
    }

    this.generating.set(true);
    this.journeyService
      .recommendImprovements(this.journeyId, {
        idempotencyKey: `journey-improvement-${this.journeyId}-${Date.now()}`
      })
      .subscribe({
        next: result => {
          this.lastGenerateResult.set(result);
          this.recommendations.set(result.recommendations);
          this.generating.set(false);
          this.messages.add({ severity: 'success', summary: this.t('growth.journey.improvement.generated') });
        },
        error: err => {
          this.generating.set(false);
          this.messages.add({ severity: 'warn', summary: this.resolveError(err) });
        }
      });
  }

  accept(rec: GrowthJourneyImprovementRecommendation): void {
    if (!this.canRecommend() || !this.isAdvisory(rec.status)) {
      return;
    }

    this.acceptingId.set(rec.id);
    this.journeyService.acceptRecommendation(rec.id).subscribe({
      next: result => {
        this.replaceRecommendation(result.recommendation);
        this.acceptingId.set(null);
        this.messages.add({ severity: 'success', summary: this.t('growth.journey.improvement.accepted') });
      },
      error: err => {
        this.acceptingId.set(null);
        this.messages.add({ severity: 'warn', summary: this.resolveError(err) });
      }
    });
  }

  dismiss(rec: GrowthJourneyImprovementRecommendation): void {
    if (!this.canRecommend() || !this.isAdvisory(rec.status)) {
      return;
    }

    this.dismissingId.set(rec.id);
    this.journeyService.dismissRecommendation(rec.id).subscribe({
      next: item => {
        this.replaceRecommendation(item);
        this.dismissingId.set(null);
        this.messages.add({ severity: 'success', summary: this.t('growth.journey.improvement.dismissed') });
      },
      error: err => {
        this.dismissingId.set(null);
        this.messages.add({ severity: 'warn', summary: this.resolveError(err) });
      }
    });
  }

  canRecommend(): boolean {
    return this.hasGrowthAction('growth.journey.improvement.recommend', 'execute');
  }

  isAdvisory(status: unknown): boolean {
    return normalizeImprovementRecommendationStatus(status) === 'Advisory';
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  providerLabel(provider: string): string {
    return this.t(swotProviderI18nKey(provider));
  }

  optimizerTypeLabelKey(type: unknown): string {
    return growthJourneyOptimizerTypeI18nKey(type);
  }

  recommendationStatusLabelKey(status: unknown): string {
    return improvementRecommendationStatusI18nKey(status);
  }

  recommendationStatusSeverity(status: unknown): 'secondary' | 'info' | 'success' | 'warn' | 'danger' | 'contrast' {
    return resolveRecStatusSeverity(status);
  }

  riskBandLabelKey(band: unknown): string {
    return improvementRiskBandI18nKey(band);
  }

  riskBandSeverity(band: unknown): 'secondary' | 'info' | 'success' | 'warn' | 'danger' | 'contrast' {
    return resolveRiskBandSeverity(band);
  }

  rationale(rec: GrowthJourneyImprovementRecommendation): string {
    return parseImprovementRationale(rec.rationaleJson);
  }

  formatConfidence(value: number): string {
    return `${Math.round(value * 100)}%`;
  }

  private replaceRecommendation(item: GrowthJourneyImprovementRecommendation): void {
    this.recommendations.update(items => items.map(row => (row.id === item.id ? item : row)));
  }

  private hasGrowthAction(permissionKey: string, requiredAction: string): boolean {
    const context = this.contextService.context();
    const roles = new Set(context?.roles ?? []);
    if (roles.has('SystemAdmin')) {
      return true;
    }

    const permission = (context?.permissions ?? []).find(item => item.key === permissionKey);
    if (!permission) {
      return false;
    }

    const actions = new Set((permission.actions ?? []).map(action => action.toLowerCase()));
    return actions.has(requiredAction.toLowerCase());
  }

  private resolveError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const body = error.error as Record<string, unknown> | null;
      if (body && typeof body['error'] === 'string') {
        return this.t(body['error']);
      }
    }

    return this.t('common.error');
  }
}
